/**
 * Tasks Page - Kanban board and list views for project task management
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, LayoutGrid, List, Search } from 'lucide-react';

// --- Types ---

interface TaskData {
  id: string;
  projectId: string;
  phaseId: string | null;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  assigneeId: string | null;
  assigneeName: string | null;
  projectName: string;
  estimatedHours: number | null;
  actualHours: number | null;
  startDate: string | null;
  dueDate: string | null;
  createdAt: string;
}

interface ProjectData {
  id: string;
  name: string;
  status: string;
}

interface TaskBoardColumn {
  status: string;
  tasks: TaskData[];
}

// --- Constants ---

const STATUSES = ['todo', 'in_progress', 'in_review', 'blocked', 'done'] as const;

const STATUS_LABELS: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  in_review: 'In Review',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
};

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  todo: 'secondary',
  in_progress: 'info',
  in_review: 'warning',
  blocked: 'destructive',
  done: 'success',
  cancelled: 'secondary',
};

const priorityVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  low: 'secondary',
  medium: 'default',
  high: 'warning',
  urgent: 'destructive',
};

const PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

const EMPTY_FORM = {
  projectId: '',
  phaseId: '',
  title: '',
  description: '',
  priority: 'medium',
  assigneeId: '',
  estimatedHours: '',
  startDate: '',
  dueDate: '',
};

// --- Component ---

export function TasksPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialProject = searchParams.get('project') || '';

  // View state
  const [view, setView] = useState<'board' | 'list'>(initialProject ? 'list' : 'list');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>(initialProject);
  const [assigneeFilter, setAssigneeFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // --- Queries ---

  const { data: projectsData } = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: async () => {
      const result = await api.listProjects({ status: 'active', limit: 100 });
      return (result.data || []) as ProjectData[];
    },
  });
  const projects = projectsData || [];

  // List view query
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['project-tasks', { page, search, status: statusFilter, projectId: projectFilter, assigneeId: assigneeFilter }],
    queryFn: async () => {
      const result = await api.listProjectTasks({
        page,
        limit: 20,
        search: search || undefined,
        status: statusFilter || undefined,
        projectId: projectFilter || undefined,
        assigneeId: assigneeFilter || undefined,
      });
      return result;
    },
    enabled: view === 'list',
  });

  const tasks = (listData?.data || []) as TaskData[];
  const pagination = listData?.pagination;

  // Board view query
  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: ['task-board', projectFilter],
    queryFn: async () => {
      const result = await api.getTaskBoard(projectFilter);
      return result.data as TaskBoardColumn[] | Record<string, TaskData[]>;
    },
    enabled: view === 'board' && !!projectFilter,
  });

  // Normalize board data into columns
  const boardColumns: Record<string, TaskData[]> = {};
  for (const s of STATUSES) {
    boardColumns[s] = [];
  }
  if (boardData) {
    if (Array.isArray(boardData)) {
      for (const col of boardData) {
        if (col.status && STATUSES.includes(col.status as typeof STATUSES[number])) {
          boardColumns[col.status] = col.tasks || [];
        }
      }
    } else {
      for (const s of STATUSES) {
        boardColumns[s] = (boardData as Record<string, TaskData[]>)[s] || [];
      }
    }
  }

  // --- Mutations ---

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createProjectTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-board'] });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      api.updateProjectTask(taskId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-board'] });
    },
  });

  // deleteMutation can be enabled when delete UI is added
  // const deleteMutation = useMutation({
  //   mutationFn: (taskId: string) => api.deleteProjectTask(taskId),
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: ['project-tasks'] });
  //     queryClient.invalidateQueries({ queryKey: ['task-board'] });
  //   },
  // });

  // --- Handlers ---

  function handleCreateSubmit() {
    if (!form.projectId || !form.title.trim()) return;
    const payload: Record<string, unknown> = {
      projectId: form.projectId,
      title: form.title.trim(),
      priority: form.priority,
    };
    if (form.phaseId) payload.phaseId = form.phaseId;
    if (form.description.trim()) payload.description = form.description.trim();
    if (form.assigneeId) payload.assigneeId = form.assigneeId;
    if (form.estimatedHours) payload.estimatedHours = parseFloat(form.estimatedHours);
    if (form.startDate) payload.startDate = form.startDate;
    if (form.dueDate) payload.dueDate = form.dueDate;
    createMutation.mutate(payload);
  }

  function handleStatusChange(taskId: string, newStatus: string) {
    updateStatusMutation.mutate({ taskId, status: newStatus });
  }

  // --- Render Helpers ---

  const isLoading = view === 'list' ? listLoading : boardLoading;

  const loadingSpinner = (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tasks</h1>
          <p className="text-muted-foreground">Manage project tasks across all projects</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-md">
            <Button
              variant={view === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
            >
              <List className="h-4 w-4 mr-1" />
              List
            </Button>
            <Button
              variant={view === 'board' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setView('board')}
            >
              <LayoutGrid className="h-4 w-4 mr-1" />
              Board
            </Button>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tasks..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
          />
        </div>

        <Select value={projectFilter} onValueChange={(v) => { setProjectFilter(v === '__all__' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Projects</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === '__all__' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
            ))}
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Assignee ID..."
          value={assigneeFilter}
          onChange={(e) => { setAssigneeFilter(e.target.value); setPage(1); }}
          className="w-[180px]"
        />
      </div>

      {/* Board requires project selection */}
      {view === 'board' && !projectFilter && (
        <Card>
          <CardContent className="py-12 text-center">
            <LayoutGrid className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">Select a Project</h3>
            <p className="text-sm text-muted-foreground">
              Choose a project from the filter above to view its task board.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && loadingSpinner}

      {/* Board View */}
      {view === 'board' && projectFilter && !boardLoading && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {STATUSES.map((status) => (
            <div key={status} className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Badge variant={statusVariant[status]}>{STATUS_LABELS[status]}</Badge>
                  <span className="text-muted-foreground text-xs">
                    {boardColumns[status].length}
                  </span>
                </h3>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {boardColumns[status].map((task) => (
                  <Card key={task.id} className="cursor-default">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-sm font-medium leading-tight">{task.title}</p>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge variant={priorityVariant[task.priority] || 'secondary'} className="text-[10px]">
                          {task.priority}
                        </Badge>
                      </div>
                      {task.assigneeName && (
                        <p className="text-xs text-muted-foreground">{task.assigneeName}</p>
                      )}
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        {(task.estimatedHours != null || task.actualHours != null) && (
                          <span>
                            {task.actualHours ?? 0}h / {task.estimatedHours ?? '?'}h
                          </span>
                        )}
                        {task.dueDate && (
                          <span>{formatDate(task.dueDate)}</span>
                        )}
                      </div>
                      {/* Status change dropdown */}
                      <Select
                        value={task.status}
                        onValueChange={(newStatus) => handleStatusChange(task.id, newStatus)}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>
                ))}
                {boardColumns[status].length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-6 border border-dashed rounded-md">
                    No tasks
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {view === 'list' && !listLoading && (
        <>
          {tasks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <List className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No tasks found</h3>
                <p className="text-sm text-muted-foreground">
                  {search || statusFilter || projectFilter
                    ? 'Try adjusting your search or filters'
                    : 'Create your first task to get started'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Assignee</TableHead>
                      <TableHead>Est Hours</TableHead>
                      <TableHead>Due Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasks.map((task) => (
                      <TableRow key={task.id}>
                        <TableCell className="font-medium max-w-[300px] truncate">
                          {task.title}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {task.projectName}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={task.status}
                            onValueChange={(newStatus) => handleStatusChange(task.id, newStatus)}
                          >
                            <SelectTrigger className="h-7 w-[130px] text-xs border-0 p-0">
                              <Badge variant={statusVariant[task.status] || 'secondary'}>
                                {STATUS_LABELS[task.status] || task.status}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                              ))}
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant={priorityVariant[task.priority] || 'secondary'}>
                            {task.priority}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {task.assigneeName || 'Unassigned'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {task.estimatedHours != null ? `${task.estimatedHours}h` : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {task.dueDate ? formatDate(task.dueDate) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Create Task Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
            <DialogDescription>
              Add a new task to a project.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Project (required) */}
            <div className="grid gap-2">
              <Label htmlFor="task-project">Project *</Label>
              <Select value={form.projectId} onValueChange={(v) => setForm({ ...form, projectId: v })}>
                <SelectTrigger id="task-project">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phase (optional) */}
            <div className="grid gap-2">
              <Label htmlFor="task-phase">Phase ID (optional)</Label>
              <Input
                id="task-phase"
                value={form.phaseId}
                onChange={(e) => setForm({ ...form, phaseId: e.target.value })}
                placeholder="Phase ID"
              />
            </div>

            {/* Title */}
            <div className="grid gap-2">
              <Label htmlFor="task-title">Title *</Label>
              <Input
                id="task-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Task title"
              />
            </div>

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="task-desc">Description</Label>
              <Textarea
                id="task-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Task description"
                rows={3}
              />
            </div>

            {/* Priority & Assignee */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="task-assignee">Assignee ID</Label>
                <Input
                  id="task-assignee"
                  value={form.assigneeId}
                  onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}
                  placeholder="User ID"
                />
              </div>
            </div>

            {/* Hours & Dates */}
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="task-hours">Est. Hours</Label>
                <Input
                  id="task-hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.estimatedHours}
                  onChange={(e) => setForm({ ...form, estimatedHours: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="task-start">Start Date</Label>
                <Input
                  id="task-start"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="task-due">Due Date</Label>
                <Input
                  id="task-due"
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={!form.projectId || !form.title.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
